/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';

import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import IconButton from 'src/components/ui/IconButton';

import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import type { UserProfileQuery } from 'src/types/__generated__/graphql';
import useAppRouter from 'src/hooks/useAppRouter';
import RelationsModalContent from 'src/components/profile/relations/RelationsModalContent';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        width: 28rem;
        height: 33rem;
        background-color: ${colours.grey.G100};
    }
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0 0 1.5rem 0;
        &.MuiDialogActions-spacing > :not(:first-of-type) {
            margin-left: 1rem;
        }
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0 1.5rem;
    }
`;

export type ViewTypes = 'relationsList' | 'searchProfile';

type ViewTypeTitles = Record<ViewTypes, string>;

interface Props {
    user: NonNullable<UserProfileQuery['userProfile']>;
    onClose: () => void;
    open: boolean;
}

const RelationsModal: React.FC<Props> = ({ user, open, onClose }) => {
    const { t } = useTranslation(['profile', 'common']);
    const router = useAppRouter();

    const [selectedView, setSelectedView] = useState<ViewTypes>('relationsList');

    const titles: ViewTypeTitles = {
        relationsList: t('profile:relations'),
        searchProfile: t('common:searchProfile'),
    };

    const closeModal = () => {
        setSelectedView('relationsList');
        onClose();
    };

    // Close modal on route changes
    useEffect(() => {
        const handleRouteChange = () => {
            closeModal();
        };
        router?.events?.on('routeChangeComplete', handleRouteChange);
        return () => {
            router?.events?.off('routeChangeComplete', handleRouteChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router?.events]);

    return (
        <StyledDialog open={open} onClose={closeModal} fullscreenOnMobile>
            <DialogTitle onClose={closeModal} component="div">
                <Box
                    width="100%"
                    display="flex"
                    justifyContent={selectedView === 'relationsList' ? 'space-between' : 'start'}
                    mr="1.5rem"
                    alignItems="center"
                >
                    {selectedView === 'searchProfile' && (
                        <Box mr="1rem">
                            <Icon
                                data-target-id="relations-modal-back-icon"
                                name="ArrowBack"
                                onClick={() => setSelectedView('relationsList')}
                            />
                        </Box>
                    )}
                    <Typography data-target-id="relations-modal-title" variant="h2">
                        {titles[selectedView]}
                    </Typography>
                    {selectedView === 'relationsList' && user && (
                        <IconButton
                            data-target-id="relations-modal-search"
                            onClick={() => setSelectedView('searchProfile')}
                            color="white"
                            style={{
                                backgroundColor: 'transparent',
                            }}
                            iconProps={{
                                name: 'Search',
                            }}
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                <RelationsModalContent type={selectedView} user={user} />
            </DialogContent>
        </StyledDialog>
    );
};

export default RelationsModal;
